<template>
  <div data-testid="details-form" class="configuration-form">
    <VerticalWizardContent :title="title">
      <label class="string-input">
        <div class="label"><Localization message="create-plan-name" /></div>
        <AlInput
          v-model="name"
          @input="input"
          :state="!$v.name.$error && null"
          :help-text="helpMessageName"
          :message="validationMessageName"
          :maxlength="nameMaxLength"
        />
      </label>
      <label class="string-input full-width">
        <div class="label">
          <Localization message="create-plan-description" />
        </div>
        <AlInput
          v-model="description"
          @input="input"
          :state="!$v.description.$error && null"
          :help-text="validationMessageDescription"
          :message="validationMessageDescription"
          :maxlength="descriptionMaxLength"
        />
      </label>
      <label
        class="string-input"
        :class="{ 'input-error': $v.permission.$error }"
      >
        <div class="label">
          <Localization message="create-plan-permission" />
        </div>
        <AlSelect
          v-model="permission"
          :options="permissions"
          :multiple="true"
          @input="input"
          :is-loading="!permissions.length"
        >
        </AlSelect>
        <div class="message">
          <Localization message="create-plan-permission-explanation" />
        </div>
      </label>
    </VerticalWizardContent>
  </div>
</template>

<script>
import { AlInput, AlSelect } from "@playvox/almendra-components";
import {
  VerticalWizardContent,
  Translator,
  Localization,
  BusinessRole,
} from "@agyletime/wfm-vue-common";

export default {
  name: "ConfigurationForm",
  components: {
    VerticalWizardContent,
    Localization,
    AlInput,
    AlSelect,
  },
  mixins: [Translator],
  props: {
    value: {
      type: Object,
      required: true,
    },
    validationsSchema: {
      type: Object,
      required: true,
    },
    submitButtonText: {
      type: String,
      default: "Submit",
    },
    showSubmitButton: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    value: {
      handler(newValue) {
        const newValues = this.handleIncomingProps(newValue);

        Object.keys(newValues).forEach((key) => {
          this[key] = newValues[key];
        });
      },
      deep: true,
    },
  },
  async created() {
    this.permissions = await this.getPermissions();
    if (this.value.permission) {
      this.permission = this.value.permission.map((v) => {
        return this.permissions.find(
          (permission) => permission.id === v._id || v.id
        );
      });
    }
  },
  data() {
    const conf = this.value;
    return this.handleIncomingProps(conf);
  },
  computed: {
    nameMaxLength() {
      return 25;
    },
    descriptionMaxLength() {
      return 50;
    },
    showNameMaxMessage() {
      return (this.name || "").length === this.nameMaxLength;
    },
    showDescriptionMaxMessage() {
      return (this.description || "").length === this.descriptionMaxLength;
    },
    helpMessageName() {
      if (this.showNameMaxMessage) {
        return this.translate("validation-max-length-reached", {
          field: this.translate("create-plan-name"),
          max: this.nameMaxLength,
        });
      }
      return "";
    },
    validationMessageName() {
      if (!this.$v.name.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-name"),
        });
      }
      if (!this.$v.name.maxLength) {
        return this.translate("validation-max-length", {
          field: this.translate("create-plan-name"),
          max: this.nameMaxLength,
        });
      }
      return "";
    },
    validationMessageDescription() {
      if (!this.$v.description.maxLength) {
        return this.translate("validation-max-length", {
          field: this.translate("create-plan-description"),
          max: this.descriptionMaxLength,
        });
      }
      if (this.showDescriptionMaxMessage) {
        return this.translate("validation-max-length-reached", {
          field: this.translate("create-plan-description"),
          max: this.descriptionMaxLength,
        });
      }
      return "";
    },
    emitObject() {
      return {
        name: this.limitStringToLength(this.name, this.nameMaxLength),
        description: this.limitStringToLength(
          this.description,
          this.descriptionMaxLength
        ),
        permission: this.permission,
      };
    },
  },
  methods: {
    onLanguageChange() {
      this.title = this.translate("create-plan-details");
    },
    getFieldState(field) {
      if (field.$error) {
        return "error";
      }
      return "";
    },
    handleIncomingProps(conf) {
      let selectedPermission = null;
      if (this.permissions && conf.permission) {
        selectedPermission = conf.permission.map((p) => {
          return this.permissions.find((permission) => permission.id === p.id);
        });
      }
      return {
        name: this.limitStringToLength(conf.name || "", this.nameMaxLength),
        description: this.limitStringToLength(
          conf.description || "",
          this.descriptionMaxLength
        ),
        permission: selectedPermission || null,
        permissions: this.permissions || [],
      };
    },
    limitStringToLength(string, length) {
      if (string.length > length) {
        return string.substring(0, length);
      }
      return string;
    },
    async getPermissions() {
      return BusinessRole.getBusinessRoles();
    },
    input() {
      this.$emit("input", this.emitObject);
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.$emit("next", this.emitObject);
    },
  },
  validations() {
    return this.validationsSchema;
  },
};
</script>

<style lang="scss" scoped>
.full-width .dr-input {
  min-width: 15rem;
  width: 60%;
}
</style>
